import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Dice6, RotateCcw, Trash2, Filter } from 'lucide-react';

const FilterPanel = ({ 
  filters, 
  onFilterChange, 
  onClearFilters, 
  onResetHistory, 
  onPickRandom, 
  matchCount,
  isRandomizing = false 
}) => {
  const [includeTagsInput, setIncludeTagsInput] = React.useState('');
  const [excludeTagsInput, setExcludeTagsInput] = React.useState('');

  // Sync input fields with filters when filters change externally
  React.useEffect(() => {
    if (filters.includeTags) {
      setIncludeTagsInput(filters.includeTags.join(', '));
    } else {
      setIncludeTagsInput('');
    }
  }, [filters.includeTags?.join(',')]);

  React.useEffect(() => {
    if (filters.excludeTags) {
      setExcludeTagsInput(filters.excludeTags.join(', '));
    } else {
      setExcludeTagsInput('');
    }
  }, [filters.excludeTags?.join(',')]);

  const handleIncludeTagsChange = (value) => {
    setIncludeTagsInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    onFilterChange({ includeTags: tags.length > 0 ? tags : null });
  };

  const handleExcludeTagsChange = (value) => {
    setExcludeTagsInput(value);
    const tags = value.split(',').map(tag => tag.trim()).filter(tag => tag);
    onFilterChange({ excludeTags: tags.length > 0 ? tags : null });
  };

  return (
    <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-sm transition-colors">
      <CardHeader className="pb-4">
        <CardTitle className="text-gray-900 dark:text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-orange-500 dark:text-orange-400" />
            <span>Filter Games</span>
          </div>
          <div className="bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-3 py-1 rounded-full text-sm font-medium">
            {matchCount} games
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Players */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Players</Label>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              min="1"
              max="20"
              value={filters.players || ''}
              onChange={(e) => onFilterChange({ players: e.target.value ? parseInt(e.target.value) : null })}
              className="w-20 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
              placeholder="Any"
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="strict-players"
                checked={filters.strictPlayers || false}
                onCheckedChange={(checked) => onFilterChange({ strictPlayers: checked })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="strict-players" className="text-sm text-gray-600 dark:text-gray-300">
                Strict (exact count)
              </Label>
            </div>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            {filters.strictPlayers ? 'Exactly this number' : 'Includes this player count'}
          </div>
        </div>

        {/* Max Playtime */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Max Playtime (minutes)</Label>
          <Input
            type="number"
            min="1"
            value={filters.maxPlaytime || ''}
            onChange={(e) => onFilterChange({ maxPlaytime: e.target.value ? parseInt(e.target.value) : null })}
            className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
            placeholder="No limit"
          />
        </div>

        {/* Complexity Range */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Complexity (1–5)</Label>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              min="1"
              max="5"
              step="0.1"
              value={filters.minComplexity || ''}
              onChange={(e) => onFilterChange({ minComplexity: e.target.value ? parseFloat(e.target.value) : null })}
              className="w-20 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
              placeholder="Min"
            />
            <span className="text-gray-400 dark:text-gray-500">to</span>
            <Input
              type="number"
              min="1"
              max="5"
              step="0.1"
              value={filters.maxComplexity || ''}
              onChange={(e) => onFilterChange({ maxComplexity: e.target.value ? parseFloat(e.target.value) : null })}
              className="w-20 bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
              placeholder="Max"
            />
          </div>
        </div>

        {/* Tags */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Include Tags</Label>
          <Input
            value={includeTagsInput}
            onChange={(e) => handleIncludeTagsChange(e.target.value)}
            className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
            placeholder="strategy, family, card game..."
          />
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Separate multiple tags with commas
          </div>
        </div>

        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Exclude Tags</Label>
          <Input
            value={excludeTagsInput}
            onChange={(e) => handleExcludeTagsChange(e.target.value)}
            className="bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-white placeholder:text-gray-500 dark:placeholder:text-gray-400 focus:border-orange-300 dark:focus:border-orange-600 focus:ring-orange-100 dark:focus:ring-orange-900/20"
            placeholder="party game, dice..."
          />
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Separate multiple tags with commas
          </div>
        </div>

        {/* Game Status Filters - Mutually Exclusive */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Game Status</Label>
          <p className="text-xs text-gray-500 dark:text-gray-400">Select one status to filter by</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Checkbox
                id="owned-only"
                checked={filters.ownedOnly || false}
                onCheckedChange={(checked) => onFilterChange({ 
                  ownedOnly: checked,
                  wishlistOnly: false,
                  tryOnly: false
                })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="owned-only" className="text-gray-700 dark:text-gray-300 cursor-pointer">Owned games only</Label>
            </div>
            
            <div className="flex items-center gap-3">
              <Checkbox
                id="wishlist-only"
                checked={filters.wishlistOnly || false}
                onCheckedChange={(checked) => onFilterChange({ 
                  wishlistOnly: checked,
                  ownedOnly: false,
                  tryOnly: false
                })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="wishlist-only" className="text-gray-700 dark:text-gray-300 cursor-pointer">Wishlist games only</Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="try-only"
                checked={filters.tryOnly || false}
                onCheckedChange={(checked) => onFilterChange({ 
                  tryOnly: checked,
                  ownedOnly: false,
                  wishlistOnly: false
                })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="try-only" className="text-gray-700 dark:text-gray-300 cursor-pointer">Want to try games only</Label>
            </div>
          </div>
        </div>

        {/* Game Type Filters */}
        <div className="space-y-3">
          <Label className="text-gray-900 dark:text-white font-medium">Game Type</Label>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <Checkbox
                id="coop-only"
                checked={filters.coOpOnly || false}
                onCheckedChange={(checked) => onFilterChange({ coOpOnly: checked })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="coop-only" className="text-gray-700 dark:text-gray-300">Co-op games only</Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="exclude-expansions"
                checked={filters.excludeExpansions || false}
                onCheckedChange={(checked) => onFilterChange({ excludeExpansions: checked })}
                className="data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500"
              />
              <Label htmlFor="exclude-expansions" className="text-gray-700 dark:text-gray-300">Exclude expansions</Label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3 pt-4 border-t border-gray-200">
          <Button
            onClick={onPickRandom}
            disabled={matchCount === 0 || isRandomizing}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white transition-colors disabled:opacity-50"
          >
            <Dice6 className={`mr-2 ${isRandomizing ? 'animate-spin' : ''}`} size={16} />
            {isRandomizing ? 'Picking...' : '🎲 Pick Random Game'}
          </Button>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="outline"
              onClick={onClearFilters}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-orange-600 dark:hover:text-orange-400 hover:border-orange-300 dark:hover:border-orange-600 px-3 py-2 text-sm h-auto flex items-center gap-2"
            >
              <Trash2 size={14} />
              Clear Filters
            </Button>
            
            <Button
              variant="outline"
              onClick={onResetHistory}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-orange-600 dark:hover:text-orange-400 hover:border-orange-300 dark:hover:border-orange-600 px-3 py-2 text-sm h-auto flex items-center gap-2"
            >
              <RotateCcw size={14} />
              Reset History
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default FilterPanel;